/**
 * Cloud Sync - Syncs localStorage data to/from Supabase app_data table
 * Provides auto-save on changes + manual save/load + conflict detection
 */

import { supabase } from "@/integrations/supabase/client";
import { addActivityEntry, settingKeyToLabel } from "@/data/activityLogStore";
import type { Json } from "@/integrations/supabase/types";
import { toast } from "sonner";
import { isAdminTimedOut, hasConflictBypass, isConflictDetectionEnabled } from "@/data/adminControlsStore";
import { useAdminSettings } from "@/stores/adminStore";
import { useStorefrontSettings } from "@/stores/storefrontSettingsStore";
import { useDeviceSettingsStore } from "@/stores/deviceSettingsStore";
import { useStockStore } from "@/stores/stockStore";
import { useSwapStore } from "@/stores/swapStore";
import { useFaqStore } from "@/stores/faqStore";
import { useAboutStore } from "@/stores/aboutStore";
import { useReviewStore } from "@/stores/reviewStore";
import { useMediaStore } from "@/stores/mediaStore";
import { useFlashSaleStore } from "@/stores/flashSaleStore";
import { useFeaturedStore } from "@/stores/featuredDeviceStore";
import { useReviewWatermarkStore } from "@/stores/reviewWatermarkStore";
import { useWatermarkStore } from "@/stores/watermarkStore";
import { useStorefrontTheme } from "@/stores/storefrontThemeStore";
import { useTutorialVideoStore } from "@/stores/tutorialVideoStore";
import { useShippingStore } from "@/stores/shippingStore";
import { useAdSlotsStore } from "@/stores/adSlotsStore";
import { useConditionGuideStore } from "@/stores/conditionGuideStore";
import { useModernTabs } from "@/stores/modernTabsStore";
import { useLiteTabs } from "@/stores/liteTabsStore";
import { useLiteMenuVisibility } from "@/data/liteMenuVisibilityStore";
import { useModernMenusStore } from "@/data/modernMenusStore";
import { useLiteFavorites } from "@/stores/liteFavoritesStore";
import { useLiteCart } from "@/stores/liteCartStore";
import { catalog, categoryOrder, BUILTIN_BRANDS } from "@/data/catalog";
import { setActiveTenantScope } from "@/lib/tenantScope";
// When non-null, all SYNC_KEYS reads/writes route to `store_tenant_data`
// filtered by this tenant_id instead of the shared `app_data` table.
// Set by `src/lib/tenantContext.ts` when a store_manager signs in or a
// `/store/:slug` storefront mounts.
let _tenantScope: string | null = null;
const TENANT_TABLE = "store_tenant_data" as const;
const MAIN_TABLE = "app_data" as const;

export interface CloudTenantPermissions {
  permissions?: Record<string, unknown> | null;
  nav_tabs?: Record<string, boolean> | null;
}

/**
 * Phase 3 — Optional branding hint used to seed a brand-new tenant's
 * empty storefront_settings with the tenant's store_title /
 * hero_heading. Set alongside the scope from tenantContext.
 */
export interface CloudTenantBrandingHint {
  store_title: string;
  hero_heading: string | null;
  description: string | null;
  logo_url: string | null;
  banner_url: string | null;
}
let _tenantBrandingHint: CloudTenantBrandingHint | null = null;

export function setCloudSyncTenantScope(
  tenantId: string | null,
  brandingHint: CloudTenantBrandingHint | null = null,
): void {
  _tenantBrandingHint = tenantId ? brandingHint : null;
  if (_tenantScope === tenantId) return;
  _tenantScope = tenantId;
  // Mirror to the lightweight scope flag so catalog/devices helpers can
  // skip platform built-ins without importing cloudSync (which would
  // create a circular import).
  try { setActiveTenantScope(tenantId); } catch {}
  // Invalidate caches so canWriteCloudData re-evaluates against the new scope.
  invalidateSessionCache();
  // Forget known timestamps — different table backing means different
  // conflict baselines.
  knownTimestamps.clear();
  recentLocalWrites.clear();
  // CRITICAL: wipe localStorage of every synced key so previous-scope data
  // (e.g. the main platform's pricing / shipping / flash-sale / storefront
  // settings) does NOT leak into the newly-attached tenant scope. Without
  // this, keys that the new tenant has no row for would survive — pending
  // changes from the old scope would skip overwrite — and Store Managers
  // would see the main store's data in their isolated admin.
  try {
    const rm = Object.getPrototypeOf(localStorage).removeItem.bind(localStorage);
    for (const key of SYNC_KEYS) {
      try { rm(key); } catch {}
    }
  } catch {}
  // Drop any pending writes — they belonged to the previous scope and must
  // not be replayed into the new tenant (or back into main).
  try { clearPendingKeys(); } catch {}
}

export function getCloudSyncTenantScope(): string | null {
  return _tenantScope;
}

/** Internal helper: which table backs the current scope. */
function activeTable(): typeof TENANT_TABLE | typeof MAIN_TABLE {
  return _tenantScope ? TENANT_TABLE : MAIN_TABLE;
}


/**
 * Reload all Zustand stores from localStorage after cloud sync.
 */
function reloadAllStores(): void {
  try {
    useAdminSettings.getState().reload();
    useStorefrontSettings.getState().reload();
    useDeviceSettingsStore.getState().reload();
    useStockStore.getState().reload();
    useSwapStore.getState().reload();
    useFaqStore.getState().reload();
    useAboutStore.getState().reload();
    useReviewStore.getState().reload();
    useMediaStore.getState().reload();
    useFlashSaleStore.getState().reload();
    useFeaturedStore.getState().reload();
    useWatermarkStore.getState().reload();
    useReviewWatermarkStore.getState().reload();
    useStorefrontTheme.getState().reload();
    useTutorialVideoStore.getState().reload();
    useShippingStore.getState().reload();
    useAdSlotsStore.getState().reload();
    useConditionGuideStore.getState().reload();
    useModernTabs.getState().reload();
    useModernMenusStore.getState().reload();
    useLiteTabs.getState().reload();
    useLiteMenuVisibility.getState().reload();
    useLiteFavorites.getState().reload();
    useLiteCart.getState().reload();
  } catch (e) {
    console.warn("reloadAllStores: some stores could not be reloaded", e);
  }
}

/**
 * Tenant storefronts must start as truly empty stores. The shared platform has
 * a built-in catalog, so a blank tenant row would otherwise fall back to the
 * main categories/devices in memory. These local defaults hide every built-in
 * item until the Store Manager explicitly restores/adds inventory.
 */
function applyEmptyTenantStorefrontDefaults(): void {
  if (!_tenantScope) return;
  const hint = _tenantBrandingHint;
  const defaults: Record<string, unknown> = {
    khays_custom_devices: [],
    khays_custom_categories: [],
    khays_custom_brands: [],
    khays_deleted_builtin_brands: BUILTIN_BRANDS,
    khays_deleted_builtin_categories: categoryOrder,
    khays_deleted_builtin_devices: catalog.map(d => d.name),
    khays_category_order: [],
    khays_device_settings: {},
    khays_stock: {},
    khays_media: {},
    khays_featured_config: { enabled: false, enabledCategories: [], excludedDevices: [] },
    khays_flash_sale: null,
    // Phase 3 — keep all platform-default content out of new tenants.
    khays_faqs: [],
    khays_about: { enabled: false, content: "" },
    khays_reviews: [],
    khays_ad_slots: {},
    khays_tutorial_video: null,
    khays_tracking_presets: [],
    khays_message_templates: [],
    khays_swap_settings: { enabled: false },
    khays_modern_menus: [],
  };
  if (hint) {
    // Seed siteTitle/siteTagline from the tenant row so any non-tenant-aware
    // consumer (admin chrome, document.title fallback, etc.) shows the
    // Store Manager's branding instead of the platform's.
    defaults.khays_storefront_settings = {
      siteTitle: hint.store_title,
      siteTagline: hint.hero_heading || hint.description || "",
    };
  }
  for (const [key, value] of Object.entries(defaults)) {
    if (localStorage.getItem(key) === null) {
      localStorage.setItem(key, JSON.stringify(value));
    }
  }
}


// All localStorage keys used by the app
const SYNC_KEYS = [
  "khays_admin_settings",
  "khays_storefront_settings",
  "khays_orders",
  "khays_stock",
  "khays_device_settings",
  "khays_removed_devices",
  "khays_deleted_builtin_devices",
  "khays_shipping_options",
  "khays_shipping_category_assignments",
  "khays_about",
  "khays_reviews",
  "khays_faqs",
  "khays_flash_sale",
  "khays_ad_slots",
  "khays_analytics",
  "khays_featured_config",
  "khays_featured_cache",
  "khays_device_links",
  "khays_media",
  "khays_media_recycled",
  "khays_category_settings",
  "khays_category_order",
  "khays_custom_sub_variants",
  "khays_sub_variant_category_settings",
  "khays_sub_variant_device_settings",
  "khays_custom_brands",
  "khays_custom_categories",
  "khays_deleted_builtin_brands",
  "khays_deleted_builtin_categories",
  "khays_extra_variants",
  "khays_variant_overrides",
  "khays_device_renames",
  "khays_brand_renames",
  "khays_category_renames",
  "khays_session_logs",
  "khays_custom_devices",
  "khays_condition_guide",
  "khays_saved_colors",
  "khays_color_templates",
  "khays_message_templates",
  "khays_deletion_audit",
  "khays_device_subcondition_modes",
  "khays_permissions_config",
  "khays_staff_notification_settings",
  "khays_recycled_orders",
  "khays_swap_settings",
  "khays_swap_pricing",
  "khays_swap_tradein_pricing",
  "khays_swap_condition_templates",
  "khays_swap_notes",
  "khays_tracking_settings",
  "khays_tracking_presets",
  "khays_auto_pricing",
  "khays_menu_visibility",
  "khays_settings_items_visibility",
  "khays_enquiries",
  "khays_activity_log",
  "khays_recent_devices",
  "khays_admin_controls",
  "khays_watermark_settings",
  "khays_review_watermark_settings",
  "khays_vault",
  "khays_storefront_theme",
  "khays_tutorial_video",
  "khays_modern_menus",
  "khays_modern_tabs",
  "khays_lite_tabs",
  "khays_lite_menu_visibility",
  "khays_notification_routing",
  // Owner-only flag controlling whether non-Lite storefront themes are exposed.
  // Must be in SYNC_KEYS so the storefront sees a hydrated value synchronously
  // (eliminates the "Classic flash" before cloud sync completes).
  "khays_themes_enabled",
  // NOTE: khays_lite_favorites and khays_lite_cart are intentionally NOT in
  // SYNC_KEYS — they are per-visitor state synced via `khays_lite_user_<uid>`
  // (handled by liteFavoritesStore / liteCartStore directly). They ARE included
  // in reloadAllStores so localStorage updates rehydrate after a cloud pull.
] as const;

/** Phase B — expose SYNC_KEYS for the localOnlyKeys boot audit. */
export function getSyncKeys(): ReadonlyArray<string> { return SYNC_KEYS as ReadonlyArray<string>; }

/** Keys that sync silently — never shown in pending UI */
const SILENT_SYNC_KEYS: ReadonlySet<string> = new Set([
  "khays_admin_controls",
  "khays_activity_log",
  "khays_session_logs",
  "khays_recent_devices",
]);

const PERSISTED_PENDING_KEY = "khays_cloud_pending_keys";

let syncInProgress = false;
let lastSyncTime = 0;
let lastWriteTime = 0;
let pendingChanges = new Set<string>();
let autoSaveTimer: ReturnType<typeof setTimeout> | null = null;
let suppressTracking = false;
let interceptorInstalled = false;
/** True once the first loadFromCloud has completed — prevents stale-default overwrites */
let initialCloudLoadDone = false;
const AUTO_SAVE_DELAY_MS = 1000;
const AUTO_SAVE_RETRY_MS = 250;
const AUTO_SAVE_ERROR_RETRY_MS = 2000;
const SAVE_STATUS_SUCCESS_VISIBLE_MS = 2500;
const MAX_SAVE_RETRIES = 5;

export type KeySaveStatusState = "pending" | "saving" | "saved" | "error" | "retrying";

export interface KeySaveStatus {
  key: string;
  state: KeySaveStatusState;
  updatedAt: number;
  attempts: number;
  error?: string;
}

const keySaveStatuses = new Map<string, KeySaveStatus>();
const keySaveStatusListeners = new Set<() => void>();
const keySaveStatusTimers = new Map<string, ReturnType<typeof setTimeout>>();
let queuedSaveKeys = new Set<string>();
let queuedSavePromise: Promise<boolean> | null = null;
let retrySaveTimer: ReturnType<typeof setTimeout> | null = null;

// --- Conflict detection ---
/** Stores the last-known updated_at per data_key from cloud */
const knownTimestamps = new Map<string, string>();

/**
 * Per-key timestamp of the most recent successful local→cloud write.
 * Used to skip overwriting a freshly-written key with a stale cloud read
 * (e.g. when realtime fires loadFromCloud before our own upsert is visible
 * to the SELECT query). Without this guard, hidden-price-combo toggles and
 * shipping option edits like `linkedTrackingPresetId` could "revert" because
 * the cloud read returned the pre-write row.
 */
const recentLocalWrites = new Map<string, number>();
const RECENT_WRITE_GUARD_MS = 5000;

function markRecentLocalWrite(keys: string[]): void {
  const now = Date.now();
  for (const k of keys) recentLocalWrites.set(k, now);
}

function isRecentLocalWrite(key: string): boolean {
  const ts = recentLocalWrites.get(key);
  if (!ts) return false;
  if (Date.now() - ts < RECENT_WRITE_GUARD_MS) return true;
  recentLocalWrites.delete(key);
  return false;
}

/** Conflict callback — set by CloudSyncProvider */
export type ConflictInfo = {
  keys: string[];
  updatedByMap: Record<string, string | null>;
  updatedAtMap: Record<string, string | null>;
  changeSummaries: Record<string, string[]>;
  onOverwrite: () => void;
  onReload: () => void;
  onDefer: () => void;
  onOverwriteKeys: (keys: string[]) => void;
  onReloadKeys: (keys: string[]) => void;
};
let conflictCallback: ((info: ConflictInfo) => void) | null = null;

export function setConflictCallback(cb: ((info: ConflictInfo) => void) | null): void {
  conflictCallback = cb;
}

function emitKeySaveStatusChange(): void {
  bumpSnapshotVersion();
  keySaveStatusListeners.forEach((listener) => {
    try {
      listener();
    } catch {}
  });
}

function clearSavedStatusTimer(key: string): void {
  const timer = keySaveStatusTimers.get(key);
  if (!timer) return;
  clearTimeout(timer);
  keySaveStatusTimers.delete(key);
}

function setKeySaveStatuses(
  keys: string[],
  state: KeySaveStatusState,
  options: { error?: string; incrementAttempts?: boolean } = {}
): void {
  const uniqueKeys = Array.from(new Set(keys));
  if (uniqueKeys.length === 0) return;

  const now = Date.now();
  let changed = false;

  uniqueKeys.forEach((key) => {
    const current = keySaveStatuses.get(key);
    const next: KeySaveStatus = {
      key,
      state,
      updatedAt: now,
      attempts: options.incrementAttempts ? (current?.attempts ?? 0) + 1 : (current?.attempts ?? 0),
      error: options.error,
    };

    if (!next.error) {
      delete next.error;
    }

    if (
      current &&
      current.state === next.state &&
      current.error === next.error &&
      current.attempts === next.attempts
    ) {
      return;
    }

    clearSavedStatusTimer(key);
    keySaveStatuses.set(key, next);

    if (state === "saved") {
      const timer = setTimeout(() => {
        const latest = keySaveStatuses.get(key);
        if (latest?.state === "saved" && !pendingChanges.has(key)) {
          keySaveStatuses.delete(key);
          keySaveStatusTimers.delete(key);
          emitKeySaveStatusChange();
        }
      }, SAVE_STATUS_SUCCESS_VISIBLE_MS);
      keySaveStatusTimers.set(key, timer);
    }

    changed = true;
  });

  if (changed) emitKeySaveStatusChange();
}

function clearKeySaveStatuses(keys?: string[]): void {
  if (!keys) {
    if (keySaveStatuses.size === 0 && keySaveStatusTimers.size === 0) return;
    keySaveStatusTimers.forEach((timer) => clearTimeout(timer));
    keySaveStatusTimers.clear();
    keySaveStatuses.clear();
    emitKeySaveStatusChange();
    return;
  }

  let changed = false;
  Array.from(new Set(keys)).forEach((key) => {
    clearSavedStatusTimer(key);
    if (keySaveStatuses.delete(key)) changed = true;
  });

  if (changed) emitKeySaveStatusChange();
}

let _cachedSnapshot: KeySaveStatus[] = [];
let _snapshotVersion = 0;
let _lastSnapshotVersion = -1;

function bumpSnapshotVersion(): void {
  _snapshotVersion++;
}

export function subscribeToSaveStatus(listener: () => void): () => void {
  keySaveStatusListeners.add(listener);
  return () => keySaveStatusListeners.delete(listener);
}

export function getSaveStatusSnapshot(): KeySaveStatus[] {
  if (_lastSnapshotVersion === _snapshotVersion) return _cachedSnapshot;
  const scope = Array.from(new Set([...pendingChanges, ...keySaveStatuses.keys()]));

  _cachedSnapshot = scope
    .map((key) => keySaveStatuses.get(key) ?? (pendingChanges.has(key)
      ? { key, state: "pending" as const, updatedAt: Date.now(), attempts: 0 }
      : null))
    .filter((entry): entry is KeySaveStatus => !!entry)
    .sort((a, b) => b.updatedAt - a.updatedAt);
  _lastSnapshotVersion = _snapshotVersion;
  return _cachedSnapshot;
}

function normalizeSyncKeys(keys: string[]): string[] {
  return Array.from(new Set(keys.filter((key): key is string => typeof key === "string" && (SYNC_KEYS as readonly string[]).includes(key))));
}

function queueKeysForSave(keys: string[], state: KeySaveStatusState = "pending"): string[] {
  const normalized = normalizeSyncKeys(keys);
  if (normalized.length === 0) return normalized;

  normalized.forEach((key) => {
    pendingChanges.add(key);
    queuedSaveKeys.add(key);
  });
  persistPendingChanges();
  setKeySaveStatuses(normalized, state);
  return normalized;
}

function scheduleRetryForKeys(keys: string[], errorMessage: string, delay: number = AUTO_SAVE_ERROR_RETRY_MS): void {
  const retryKeys = normalizeSyncKeys(keys).filter((key) => pendingChanges.has(key));
  if (retryKeys.length === 0) return;

  // Check if any key has exceeded max retries
  const exhaustedKeys = retryKeys.filter((key) => {
    const status = keySaveStatuses.get(key);
    return status && status.attempts >= MAX_SAVE_RETRIES;
  });
  const retriableKeys = retryKeys.filter((key) => !exhaustedKeys.includes(key));

  if (exhaustedKeys.length > 0) {
    setKeySaveStatuses(exhaustedKeys, "error", { error: `Failed after ${MAX_SAVE_RETRIES} attempts` });
    toast.error(`${exhaustedKeys.length} setting(s) failed to save after ${MAX_SAVE_RETRIES} attempts. Use the retry button to try again.`);
  }

  if (retriableKeys.length === 0) return;

  retriableKeys.forEach((key) => queuedSaveKeys.add(key));
  persistPendingChanges();

  if (retrySaveTimer) clearTimeout(retrySaveTimer);
  retrySaveTimer = setTimeout(() => {
    retrySaveTimer = null;
    const keysToRetry = retriableKeys.filter((key) => pendingChanges.has(key));
    if (keysToRetry.length === 0) return;
    setKeySaveStatuses(keysToRetry, "retrying", { error: errorMessage });
    void processQueuedSaveKeys();
  }, delay);
}

async function processQueuedSaveKeys(): Promise<boolean> {
  if (retrySaveTimer) {
    clearTimeout(retrySaveTimer);
    retrySaveTimer = null;
  }

  if (queuedSavePromise) return queuedSavePromise;

  queuedSavePromise = (async () => {
    let overallSuccess = true;

    while (queuedSaveKeys.size > 0) {
      const keys = Array.from(queuedSaveKeys);
      queuedSaveKeys.clear();
      const success = await saveKeysToCloudNow(keys);
      overallSuccess = overallSuccess && success;
      if (!success) break;
    }

    return overallSuccess;
  })().finally(() => {
    queuedSavePromise = null;
    if (queuedSaveKeys.size > 0) {
      void processQueuedSaveKeys();
    }
  });

  return queuedSavePromise;
}

export async function retrySaveKeys(keys: string[]): Promise<boolean> {
  // Reset attempt counters for manual retry
  const normalized = normalizeSyncKeys(keys);
  normalized.forEach((key) => {
    const status = keySaveStatuses.get(key);
    if (status) keySaveStatuses.set(key, { ...status, attempts: 0 });
  });
  const queued = queueKeysForSave(normalized, "retrying");
  if (queued.length === 0) return true;
  return processQueuedSaveKeys();
}

function persistPendingChanges(): void {
  try {
    if (pendingChanges.size === 0) {
      localStorage.removeItem(PERSISTED_PENDING_KEY);
      return;
    }

    localStorage.setItem(PERSISTED_PENDING_KEY, JSON.stringify(Array.from(pendingChanges)));
  } catch {}
}

function hydratePendingChanges(): void {
  try {
    const raw = localStorage.getItem(PERSISTED_PENDING_KEY);
    if (!raw) return;

    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return;

    pendingChanges = new Set(
      parsed.filter(
        (key): key is string => typeof key === "string" && (SYNC_KEYS as readonly string[]).includes(key)
      )
    );
  } catch {}
}

function clearPendingKeys(keys?: string[]): void {
  if (!keys) {
    pendingChanges.clear();
    queuedSaveKeys.clear();
    clearKeySaveStatuses();
  } else {
    const uniqueKeys = Array.from(new Set(keys));
    uniqueKeys.forEach((key) => {
      pendingChanges.delete(key);
      queuedSaveKeys.delete(key);
    });
    clearKeySaveStatuses(uniqueKeys);
  }
  persistPendingChanges();
}

function clearPendingKeysIfUnchanged(snapshot: Record<string, string | null>): { clearedKeys: string[]; remainingKeys: string[] } {
  const clearedKeys: string[] = [];
  const remainingKeys: string[] = [];

  Object.entries(snapshot).forEach(([key, writtenValue]) => {
    const currentValue = localStorage.getItem(key);
    if (currentValue === writtenValue) {
      pendingChanges.delete(key);
      queuedSaveKeys.delete(key);
      clearedKeys.push(key);
    } else {
      remainingKeys.push(key);
    }
  });
  persistPendingChanges();
  return { clearedKeys, remainingKeys };
}

// Cache for write access check — avoids querying user_roles on every single save
let _cachedCanWrite: boolean | null = null;
let _canWriteCacheTime = 0;
const CAN_WRITE_CACHE_TTL = 30_000; // 30 seconds

async function canWriteCloudData(): Promise<boolean> {
  const now = Date.now();
  if (_cachedCanWrite !== null && now - _canWriteCacheTime < CAN_WRITE_CACHE_TTL) {
    return _cachedCanWrite;
  }
  try {
    const userId = await getCurrentUserId();
    if (!userId) { _cachedCanWrite = false; _canWriteCacheTime = now; return false; }

    if (await isCurrentUserTimedOut()) { _cachedCanWrite = false; _canWriteCacheTime = now; return false; }

    const { data, error } = await supabase
      .from("user_roles")
      .select("role")
      .eq("user_id", userId)
      .maybeSingle();

    if (error) { _cachedCanWrite = false; _canWriteCacheTime = now; return false; }
    if (_tenantScope) {
      // In tenant mode only the tenant owner (store_manager) can write,
      // plus platform admins who maintain tenants on the owner's behalf.
      _cachedCanWrite =
        data?.role === "store_manager" ||
        data?.role === "super_admin" ||
        data?.role === "manager";
    } else {
      _cachedCanWrite = data?.role === "super_admin" || data?.role === "manager";
    }
    _canWriteCacheTime = now;
    return _cachedCanWrite;
  } catch {
    _cachedCanWrite = false;
    _canWriteCacheTime = now;
    return false;
  }
}

// Session cache to avoid navigator lock contention from concurrent getSession() calls
let _cachedSessionUserId: string | null = null;
let _sessionCacheTime = 0;
const SESSION_CACHE_TTL = 10_000; // 10 seconds

/** Get the current user's ID from cached session — avoids lock contention */
async function getCurrentUserId(): Promise<string | null> {
  const now = Date.now();
  if (_cachedSessionUserId && now - _sessionCacheTime < SESSION_CACHE_TTL) {
    return _cachedSessionUserId;
  }
  try {
    const { data } = await supabase.auth.getSession();
    _cachedSessionUserId = data.session?.user?.id ?? null;
    _sessionCacheTime = now;
    return _cachedSessionUserId;
  } catch {
    return _cachedSessionUserId; // return stale cache on error
  }
}

/** Invalidate session cache (call on sign-in/sign-out) */
export function invalidateSessionCache(): void {
  _cachedSessionUserId = null;
  _sessionCacheTime = 0;
  _cachedCanWrite = null;
  _canWriteCacheTime = 0;
}

/** Check if current user is timed out (server-authoritative). */
export async function isCurrentUserTimedOut(): Promise<boolean> {
  const userId = await getCurrentUserId();
  if (!userId) return false;
  try {
    const { data } = await supabase.rpc("get_my_admin_control" as any);
    const row: any = data || {};
    const timedOut = !!row.timedOut;
    const until = Number(row.timedOutUntil || 0);
    if (timedOut && (until === 0 || Date.now() < until)) {
      _cachedCanWrite = false;
      _canWriteCacheTime = Date.now();
      return true;
    }
  } catch {
    // fall through to local check
  }
  return isAdminTimedOut(userId);
}

/** Check if current user has conflict bypass */
export async function currentUserHasBypass(): Promise<boolean> {
  const userId = await getCurrentUserId();
  if (!userId) return false;
  try {
    const { data } = await supabase.rpc("get_my_admin_control" as any);
    if ((data as any)?.bypassConflicts) return true;
  } catch {}
  return hasConflictBypass(userId);
}

/** Check if current user is a super_admin (owner) */
export async function isCurrentUserOwner(): Promise<boolean> {
  const userId = await getCurrentUserId();
  if (!userId) return false;
  try {
    const { data } = await supabase
      .from("user_roles")
      .select("role")
      .eq("user_id", userId)
      .maybeSingle();
    return data?.role === "super_admin";
  } catch { return false; }
}

/**
 * Load all app data from cloud into localStorage
 */
export async function loadFromCloud(): Promise<{ success: boolean; keysLoaded: number }> {
  try {
    hydratePendingChanges();

    const tenantId = _tenantScope;
    let q: any = supabase
      .from(activeTable() as any)
      .select("data_key, data_value, updated_at")
      .in("data_key", [...SYNC_KEYS]);
    if (tenantId) q = q.eq("tenant_id", tenantId);
    const { data, error } = (await q) as {
      data: { data_key: string; data_value: Json; updated_at: string }[] | null;
      error: any;
    };

    if (error) throw error;

    suppressTracking = true;
    const originalRemoveItem = Object.getPrototypeOf(localStorage).removeItem.bind(localStorage);
    const cloudKeys = new Set<string>();

    let keysLoaded = 0;
    for (const row of (data || [])) {
      try {
        cloudKeys.add(row.data_key);
        if (pendingChanges.has(row.data_key)) {
          continue;
        }
        // Skip overwriting a key we just successfully wrote — the cloud SELECT
        // may return a pre-write snapshot (read replica lag / realtime ordering),
        // which would visibly "revert" the user's change.
        if (isRecentLocalWrite(row.data_key)) {
          // Still update the known timestamp so subsequent conflict checks pass.
          if (row.updated_at) knownTimestamps.set(row.data_key, row.updated_at);
          continue;
        }

        const value = typeof row.data_value === "string"
          ? row.data_value
          : JSON.stringify(row.data_value);
        localStorage.setItem(row.data_key, value);
        // Track the cloud timestamp for conflict detection
        if (row.updated_at) {
          knownTimestamps.set(row.data_key, row.updated_at);
        }
        keysLoaded++;
      } catch (e) {
        console.warn(`Failed to load key ${row.data_key}:`, e);
      }
    }

    for (const key of SYNC_KEYS) {
      if (cloudKeys.has(key) || pendingChanges.has(key)) continue;
      if (localStorage.getItem(key) === null) continue;

      try {
        originalRemoveItem(key);
        knownTimestamps.delete(key);
      } catch (e) {
        console.warn(`Failed to clear stale key ${key}:`, e);
      }
    }

    applyEmptyTenantStorefrontDefaults();

    lastSyncTime = Date.now();
    initialCloudLoadDone = true;
    // Reload all Zustand stores so React re-renders with fresh data
    reloadAllStores();
    return { success: true, keysLoaded };
  } catch (e) {
    console.error("Failed to load from cloud:", e);
    // Even when the initial load fails (offline / RLS / timeout), unblock the
    // auto-save tracker so subsequent admin edits queue locally and push to
    // cloud once connectivity returns. Without this, settings like the
    // shipping panel silently revert after a reload because trackChange()
    // gates on initialCloudLoadDone.
    initialCloudLoadDone = true;
    return { success: false, keysLoaded: 0 };
  } finally {
    suppressTracking = false;
  }
}

/**
 * Check for conflicts before saving — returns conflicting keys with change summaries
 */
async function detectConflicts(keys: string[]): Promise<{ conflicting: string[]; updatedByMap: Record<string, string | null>; updatedAtMap: Record<string, string | null>; changeSummaries: Record<string, string[]> }> {
  if (keys.length === 0) return { conflicting: [], updatedByMap: {}, updatedAtMap: {}, changeSummaries: {} };

  try {
    const tenantId = _tenantScope;
    let q: any = supabase
      .from(activeTable() as any)
      .select("data_key, data_value, updated_at, updated_by")
      .in("data_key", keys);
    if (tenantId) q = q.eq("tenant_id", tenantId);
    const { data, error } = (await q) as {
      data: { data_key: string; data_value: Json; updated_at: string; updated_by: string | null }[] | null;
      error: any;
    };

    if (error || !data) return { conflicting: [], updatedByMap: {}, updatedAtMap: {}, changeSummaries: {} };

    const conflicting: string[] = [];
    const updatedByMap: Record<string, string | null> = {};
    const updatedAtMap: Record<string, string | null> = {};
    const changeSummaries: Record<string, string[]> = {};

    for (const row of data) {
      const known = knownTimestamps.get(row.data_key);
      if (known && row.updated_at && row.updated_at !== known) {
        conflicting.push(row.data_key);
        updatedByMap[row.data_key] = row.updated_by ?? null;
        updatedAtMap[row.data_key] = row.updated_at ?? null;
        // Generate change summary by diffing local vs cloud
        changeSummaries[row.data_key] = generateChangeSummary(row.data_key, row.data_value);
      }
    }
    return { conflicting, updatedByMap, updatedAtMap, changeSummaries };
  } catch {
    return { conflicting: [], updatedByMap: {}, updatedAtMap: {}, changeSummaries: {} };
  }
}

/**
 * Generate a human-readable summary of what changed between local and cloud data
 */
function generateChangeSummary(key: string, cloudValue: Json): string[] {
  const summaries: string[] = [];
  try {
    const localRaw = localStorage.getItem(key);
    if (!localRaw) return ["Local data not found"];
    const local = JSON.parse(localRaw);
    const cloud = typeof cloudValue === "string" ? JSON.parse(cloudValue) : cloudValue;

    if (typeof local !== "object" || typeof cloud !== "object" || !local || !cloud) {
      return ["Value was modified"];
    }

    // For arrays (orders, enquiries, reviews, etc.)
    if (Array.isArray(local) && Array.isArray(cloud)) {
      // Try to identify items by id/name fields for detailed diff
      const idField = local[0]?.id ? "id" : local[0]?.name ? "name" : local[0]?.deviceName ? "deviceName" : null;
      if (idField) {
        const localMap = new Map(local.map((item: any) => [item[idField], item]));
        const cloudMap = new Map(cloud.map((item: any) => [item[idField], item]));
        const added: string[] = [];
        const removed: string[] = [];
        const modified: string[] = [];
        for (const [id, item] of localMap) {
          if (!cloudMap.has(id)) added.push(getItemLabel(item));
          else if (JSON.stringify(item) !== JSON.stringify(cloudMap.get(id))) modified.push(getItemLabel(item));
        }
        for (const [id, item] of cloudMap) {
          if (!localMap.has(id)) removed.push(getItemLabel(item));
        }
        if (added.length > 0) {
          summaries.push(`Added: ${added.slice(0, 3).join(", ")}${added.length > 3 ? ` +${added.length - 3} more` : ""}`);
        }
        if (removed.length > 0) {
          summaries.push(`Removed: ${removed.slice(0, 3).join(", ")}${removed.length > 3 ? ` +${removed.length - 3} more` : ""}`);
        }
        if (modified.length > 0) {
          summaries.push(`Modified: ${modified.slice(0, 3).join(", ")}${modified.length > 3 ? ` +${modified.length - 3} more` : ""}`);
        }
        if (summaries.length === 0) summaries.push(`${local.length} items (no field changes detected)`);
      } else {
        const diff = local.length - cloud.length;
        if (diff > 0) summaries.push(`${diff} item(s) added locally`);
        else if (diff < 0) summaries.push(`${Math.abs(diff)} item(s) added remotely`);
        else summaries.push(`${local.length} items modified`);
      }
      return summaries;
    }

    // For objects — compare top-level keys
    const allKeys = new Set([...Object.keys(local), ...Object.keys(cloud)]);
    const changed: string[] = [];
    const added: string[] = [];
    const removed: string[] = [];

    for (const k of allKeys) {
      const inLocal = k in local;
      const inCloud = k in cloud;
      if (inLocal && inCloud) {
        if (JSON.stringify(local[k]) !== JSON.stringify(cloud[k])) {
          changed.push(formatFieldName(k));
        }
      } else if (inLocal && !inCloud) {
        added.push(formatFieldName(k));
      } else {
        removed.push(formatFieldName(k));
      }
    }

    // Specific handling for pricing/device keys
    if (key === "khays_admin_settings" || key === "khays_swap_pricing") {
      // Drill into pricing objects for more detail
      const pricingKeys = ["prices", "priceOverrides", "marginPercent", "commissionPercent"];
      for (const pk of pricingKeys) {
        if (local[pk] && cloud[pk] && JSON.stringify(local[pk]) !== JSON.stringify(cloud[pk])) {
          if (typeof local[pk] === "object" && typeof cloud[pk] === "object") {
            const changedDevices = Object.keys(local[pk]).filter(
              d => JSON.stringify(local[pk][d]) !== JSON.stringify(cloud[pk]?.[d])
            );
            if (changedDevices.length > 0) {
              summaries.push(`${formatFieldName(pk)}: ${changedDevices.length} device(s) updated`);
              // Show up to 3 device names
              const preview = changedDevices.slice(0, 3).map(d => formatFieldName(d));
              summaries.push(`  → ${preview.join(", ")}${changedDevices.length > 3 ? ` +${changedDevices.length - 3} more` : ""}`);
            }
          }
        }
      }
    }

    if (key === "khays_device_settings") {
      // Show which devices had visibility toggled
      if (local && cloud) {
        const toggledDevices: string[] = [];
        for (const dk of Object.keys(local)) {
          if (typeof local[dk] === "object" && typeof cloud[dk] === "object" && local[dk] && cloud[dk]) {
            if (local[dk].enabled !== cloud[dk]?.enabled) {
              toggledDevices.push(formatFieldName(dk));
            }
          }
        }
        if (toggledDevices.length > 0) {
          summaries.push(`${toggledDevices.length} device(s) toggled on/off`);
          const preview = toggledDevices.slice(0, 3);
          summaries.push(`  → ${preview.join(", ")}${toggledDevices.length > 3 ? ` +${toggledDevices.length - 3} more` : ""}`);
        }
      }
    }

    if (key === "khays_stock") {
      const stockChanges: string[] = [];
      for (const sk of Object.keys(local)) {
        if (JSON.stringify(local[sk]) !== JSON.stringify(cloud[sk])) {
          stockChanges.push(formatFieldName(sk));
        }
      }
      if (stockChanges.length > 0) {
        summaries.push(`Stock updated for ${stockChanges.length} device(s)`);
        const preview = stockChanges.slice(0, 3);
        summaries.push(`  → ${preview.join(", ")}${stockChanges.length > 3 ? ` +${stockChanges.length - 3} more` : ""}`);
      }
    }

    // If no specific summaries were generated, use generic field-level diff
    if (summaries.length === 0) {
      if (changed.length > 0) summaries.push(`Updated: ${changed.slice(0, 4).join(", ")}${changed.length > 4 ? ` +${changed.length - 4} more` : ""}`);
      if (added.length > 0) summaries.push(`Added: ${added.slice(0, 3).join(", ")}${added.length > 3 ? ` +${added.length - 3} more` : ""}`);
      if (removed.length > 0) summaries.push(`Removed: ${removed.slice(0, 3).join(", ")}${removed.length > 3 ? ` +${removed.length - 3} more` : ""}`);
    }

    if (summaries.length === 0) summaries.push("Settings were modified");
  } catch {
    summaries.push("Changes detected");
  }
  return summaries;
}

function formatFieldName(name: string): string {
  return name.replace(/([A-Z])/g, " $1").replace(/[_-]/g, " ").replace(/\b\w/g, c => c.toUpperCase()).trim();
}

/** Get a human-readable label for an array item */
function getItemLabel(item: any): string {
  if (!item || typeof item !== "object") return String(item);
  // Try common label fields in order of specificity
  return item.deviceName || item.name || item.label || item.title || item.question || item.username || item.id || "item";
}

/**
 * Save all localStorage data to cloud
 */
export async function saveToCloud(): Promise<{ success: boolean; keysSaved: number }> {
  const keys = [...SYNC_KEYS] as string[];
  const success = await saveKeysToCloud(keys);
  const keysSaved = keys.filter((key) => localStorage.getItem(key) !== null).length;
  return { success, keysSaved: success ? keysSaved : 0 };
}

/**
 * Save specific changed keys to cloud (with conflict detection)
 */
export async function saveKeysToCloud(keys: string[]): Promise<boolean> {
  const normalizedKeys = queueKeysForSave(keys);
  if (normalizedKeys.length === 0) return true;
  return processQueuedSaveKeys();
}

async function saveKeysToCloudNow(keys: string[]): Promise<boolean> {
  const normalizedKeys = normalizeSyncKeys(keys);
  if (normalizedKeys.length === 0) return true;

  syncInProgress = true;
  setKeySaveStatuses(normalizedKeys, "saving");

  try {
    if (!(await canWriteCloudData())) {
      setKeySaveStatuses(normalizedKeys, "error", {
        error: "Session expired or write access is unavailable.",
        incrementAttempts: true,
      });
      return false;
    }

    // Check if user has conflict bypass, is owner, or conflict detection disabled globally
    const userId = await getCurrentUserId();
    const bypass = userId ? hasConflictBypass(userId) : false;
    const ownerBypass = await isCurrentUserOwner();

    // Check for conflicts (unless bypassed, owner, or globally disabled)
    const { conflicting, updatedByMap, updatedAtMap, changeSummaries } = (bypass || ownerBypass || !isConflictDetectionEnabled())
      ? { conflicting: [], updatedByMap: {}, updatedAtMap: {}, changeSummaries: {} }
      : await detectConflicts(normalizedKeys);
    if (conflicting.length > 0 && conflictCallback) {
      syncInProgress = false;
      setKeySaveStatuses(conflicting, "pending");
      return new Promise<boolean>((resolve) => {
        conflictCallback!({
          keys: conflicting,
          updatedByMap,
          updatedAtMap,
          changeSummaries,
          onOverwrite: async () => {
            const result = await forceWriteKeys(normalizedKeys);
            resolve(result);
          },
          onReload: async () => {
            await loadFromCloud();
            clearPendingKeys(normalizedKeys);
            resolve(true);
          },
          onDefer: () => {
            resolve(false);
          },
          onOverwriteKeys: async (selectedKeys: string[]) => {
            const reloadKeys = conflicting.filter(k => !selectedKeys.includes(k));
            if (selectedKeys.length > 0) await forceWriteKeys(selectedKeys);
            if (reloadKeys.length > 0) clearPendingKeys(reloadKeys);
            await loadFromCloud();
            resolve(true);
          },
          onReloadKeys: async (selectedKeys: string[]) => {
            clearPendingKeys(selectedKeys);
            await loadFromCloud();
            resolve(true);
          },
        });
      });
    }

    const success = await forceWriteKeys(normalizedKeys);
    if (!success) {
      const errorMessage = "Could not save to cloud.";
      setKeySaveStatuses(normalizedKeys, "error", { error: errorMessage, incrementAttempts: true });
      scheduleRetryForKeys(normalizedKeys, `${errorMessage} Retrying…`);
    }
    return success;
  } catch (e) {
    console.error("Failed to save keys to cloud:", e);
    const errorMessage = e instanceof Error ? e.message : "Could not save to cloud.";
    setKeySaveStatuses(normalizedKeys, "error", { error: errorMessage, incrementAttempts: true });
    scheduleRetryForKeys(normalizedKeys, `${errorMessage} Retrying…`);
    return false;
  } finally {
    syncInProgress = false;
  }
}

/** Write keys without conflict check (used after user confirms overwrite) */
async function forceWriteKeys(keys: string[]): Promise<boolean> {
  const wasSyncing = syncInProgress;
  if (!wasSyncing) syncInProgress = true;
  setKeySaveStatuses(keys, "saving");

  try {
    const hasWriteAccess = await canWriteCloudData();
    if (!hasWriteAccess) {
      if (!wasSyncing) {
        setKeySaveStatuses(keys, "error", {
          error: "Session expired or write access is unavailable.",
          incrementAttempts: true,
        });
      }
      return false;
    }

    // Tenant bleed guard — block store_manager writes to the shared `app_data`
    // table when no tenant scope is active. This prevents one store's data
    // from leaking into the main site if tenant resolution failed.
    if (!_tenantScope) {
      try {
        const uid = await getCurrentUserId();
        if (uid) {
          const { data: roleRow } = await supabase
            .from("user_roles").select("role").eq("user_id", uid).maybeSingle();
          if (roleRow?.role === "store_manager") {
            console.warn("[cloudSync] Blocked store_manager write without tenant scope:", keys);
            setKeySaveStatuses(keys, "error", {
              error: "Tenant scope not resolved — write blocked to prevent cross-store data bleed.",
              incrementAttempts: true,
            });
            return false;
          }
        }
      } catch { /* fall through */ }
    }


    const rows: { data_key: string; data_value: Json; updated_at: string }[] = [];
    const deletedKeys: string[] = [];
    const writtenSnapshot: Record<string, string | null> = {};
    const now = new Date().toISOString();

    for (const key of keys) {
      const raw = localStorage.getItem(key);
      writtenSnapshot[key] = raw;
      if (raw !== null) {
        let parsed: Json;
        try {
          parsed = JSON.parse(raw);
        } catch {
          parsed = raw;
        }
        rows.push({ data_key: key, data_value: parsed, updated_at: now });
      } else {
        deletedKeys.push(key);
      }
    }

    const tenantId = _tenantScope;

    if (rows.length > 0) {
      const payload: any[] = tenantId
        ? rows.map(r => ({ ...r, tenant_id: tenantId }))
        : rows;
      const { error } = await supabase
        .from(activeTable() as any)
        .upsert(payload, {
          onConflict: tenantId ? "tenant_id,data_key" : "data_key",
        });
      if (error) throw error;
    }

    if (deletedKeys.length > 0) {
      let dq: any = supabase
        .from(activeTable() as any)
        .delete()
        .in("data_key", deletedKeys);
      if (tenantId) dq = dq.eq("tenant_id", tenantId);
      const { error } = await dq;
      if (error) throw error;
    }

    // Update known timestamps
    for (const row of rows) {
      knownTimestamps.set(row.data_key, row.updated_at);
    }
    deletedKeys.forEach((key) => knownTimestamps.delete(key));
    // Mark these keys so a stale realtime/poll loadFromCloud within the next
    // few seconds doesn't overwrite them with a pre-write snapshot.
    markRecentLocalWrite([...rows.map(r => r.data_key), ...deletedKeys]);

    const { clearedKeys, remainingKeys } = clearPendingKeysIfUnchanged(writtenSnapshot);
    // Show toast if keys were previously in error/retrying state (successful retry)
    const retriedKeys = clearedKeys.filter((key) => {
      const prev = keySaveStatuses.get(key);
      return prev && (prev.state === "retrying" || prev.state === "error") && prev.attempts > 0;
    });

    setKeySaveStatuses(clearedKeys, "saved");
    setKeySaveStatuses(remainingKeys, "pending");

    if (retriedKeys.length > 0) {
      toast.success(`${retriedKeys.length} setting(s) saved successfully after retry.`);
    }

    lastSyncTime = Date.now();
    lastWriteTime = Date.now();

    // Auto-log activity for admin saves (skip activity_log key itself to prevent loops)
    const logKeys = keys.filter(k => k !== "khays_activity_log" && k !== "khays_analytics" && k !== "khays_session_logs");
    if (logKeys.length > 0 && hasWriteAccess) {
      try {
        const userId = await getCurrentUserId();
        const username = userId ? "Admin" : "Admin";
        for (const key of logKeys) {
          addActivityEntry({
            username,
            action: "updated",
            target: settingKeyToLabel(key),
          });
        }
      } catch {}
    }

    reloadAllStores();
    return true;
  } catch (e) {
    console.error("Failed to force-write keys to cloud:", e);
    if (!wasSyncing) {
      const errorMessage = e instanceof Error ? e.message : "Could not save to cloud.";
      setKeySaveStatuses(keys, "error", { error: errorMessage, incrementAttempts: true });
    }
    return false;
  } finally {
    if (!wasSyncing) syncInProgress = false;
  }
}

/**
 * Track a localStorage change for auto-save
 */
export function trackChange(key: string): void {
  if (!SYNC_KEYS.includes(key as any)) return;
  // Don't track changes until the first cloud load has completed
  // This prevents stale defaults from overwriting cloud data
  if (!initialCloudLoadDone) return;
  pendingChanges.add(key);
  persistPendingChanges();
  setKeySaveStatuses([key], "pending");
  scheduleAutoSave();
}

/**
 * Schedule auto-save (debounced - saves 1s after last change, then auto-pushes)
 */
function scheduleAutoSave(): void {
  if (autoSaveTimer) clearTimeout(autoSaveTimer);
  autoSaveTimer = setTimeout(async () => {
    autoSaveTimer = null;
    if (pendingChanges.size > 0) {
      if (syncInProgress) {
        scheduleAutoSaveWithDelay(AUTO_SAVE_RETRY_MS);
        return;
      }
      const keys = Array.from(pendingChanges);
      await saveKeysToCloud(keys);
    }
  }, AUTO_SAVE_DELAY_MS);
}

function scheduleAutoSaveWithDelay(delay: number): void {
  if (autoSaveTimer) clearTimeout(autoSaveTimer);
  autoSaveTimer = setTimeout(async () => {
    autoSaveTimer = null;
    if (pendingChanges.size === 0) return;
    if (syncInProgress) {
      scheduleAutoSaveWithDelay(AUTO_SAVE_RETRY_MS);
      return;
    }
    const keys = Array.from(pendingChanges);
    await saveKeysToCloud(keys);
  }, delay);
}

/**
 * Get sync status info
 */
export function getSyncStatus(): {
  hasPendingChanges: boolean;
  pendingCount: number;
  pendingKeys: string[];
  lastSyncTime: number;
  lastWriteTime: number;
  isSyncing: boolean;
} {
 const visiblePending = Array.from(pendingChanges).filter(k => !SILENT_SYNC_KEYS.has(k));
  return {
    hasPendingChanges: visiblePending.length > 0,
    pendingCount: visiblePending.length,
    pendingKeys: visiblePending,
    lastSyncTime,
    lastWriteTime,
    isSyncing: syncInProgress,
  };
}

/**
 * Discard a pending change — reverts the key from cloud (or removes local-only)
 */
export async function discardPendingKey(key: string): Promise<void> {
  clearPendingKeys([key]);
  // Reload that key from cloud
  try {
    const tenantId = _tenantScope;
    let q: any = supabase
      .from(activeTable() as any)
      .select("data_key, data_value, updated_at")
      .eq("data_key", key);
    if (tenantId) q = q.eq("tenant_id", tenantId);
    const { data } = (await q.maybeSingle()) as {
      data: { data_value: Json; updated_at: string } | null;
    };
    if (data) {
      const value = typeof data.data_value === "string"
        ? data.data_value
        : JSON.stringify(data.data_value);
      const origSet = Object.getPrototypeOf(localStorage).setItem.bind(localStorage);
      // Use suppressTracking to avoid re-tracking
      suppressTracking = true;
      origSet(key, value);
      suppressTracking = false;
      if (data.updated_at) knownTimestamps.set(key, data.updated_at);
    }
  } catch (e) {
    console.warn("discardPendingKey: failed to reload from cloud", e);
  }
  reloadAllStores();
}

/**
 * Intercept localStorage.setItem to auto-track changes
 */
export function installSyncInterceptor(): void {
  if (interceptorInstalled) return;
  interceptorInstalled = true;
  hydratePendingChanges();

  const originalSetItem = localStorage.setItem.bind(localStorage);
  const originalRemoveItem = localStorage.removeItem.bind(localStorage);

  localStorage.setItem = (key: string, value: string) => {
    originalSetItem(key, value);
    if (!suppressTracking) {
      trackChange(key);
    }
  };

  localStorage.removeItem = (key: string) => {
    originalRemoveItem(key);
    if (!suppressTracking) {
      trackChange(key);
    }
  };
}

/**
 * Force flush any pending changes immediately
 */
export async function flushPendingChanges(): Promise<boolean> {
  if (pendingChanges.size === 0) return true;
  if (autoSaveTimer) clearTimeout(autoSaveTimer);
  const keys = Array.from(pendingChanges);
  return saveKeysToCloud(keys);
}

/**
 * Force sync ALL keys (including silent ones) to cloud — for debugging.
 * Returns the list of keys that were synced.
 */
export async function forceSyncAll(): Promise<{ success: boolean; syncedKeys: string[] }> {
  if (autoSaveTimer) clearTimeout(autoSaveTimer);
  const keys = [...SYNC_KEYS];
  const success = await forceWriteKeys(keys);
  return { success, syncedKeys: keys as unknown as string[] };
}

/** Human-readable label for a sync key */
export function prettySyncKey(key: string): string {
  return key
    .replace(/^khays_/, "")
    .replace(/_/g, " ")
    .replace(/\b\w/g, c => c.toUpperCase());
}
